import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getActions } from 'api/actionsClient';
import type { ActionContract } from 'model/actions';
import type { WorkflowNode, WorkflowEdge, WorkflowEdgeData } from 'model/xyflowMapping';
import { SPECIAL_ACTIONS } from 'model/specialActions';
import type { InputBinding } from 'model/workflow';
import { useStepIdEditor } from 'hooks/useStepIdEditor';

import OutputsSection from './OutputsSection';
import InputsSection from './InputsSection';
import SettingsSection from './SettingsSection';
import ConnectionsList from './ConnectionsList';
import RoutesEditor, { type Route } from './RoutesEditor';
import Styled from './NodeSidePanel.styled';

const {
  StyledEmpty,
  StyledError,
  StyledLoading,
  StyledStepIdInput,
  StyledActionLine,
  StyledActionDisplay,
  StyledActionId,
  StyledSection,
  StyledSectionLabel,
} = Styled;

interface OwnProps {
  node: WorkflowNode | null;
  allNodes: WorkflowNode[];
  allEdges: WorkflowEdge[];
  onNodeChange: (node: WorkflowNode) => void;
  onRenameStep: (oldId: string, newId: string) => void;
  onEdgeDataChange: (edgeId: string, data: Partial<WorkflowEdgeData>) => void;
  onDeleteEdge: (edgeId: string) => void;
  onAddEdge: (sourceId: string, targetId: string) => void;
}

const NodeSidePanel = ({
  node,
  allNodes,
  allEdges,
  onNodeChange,
  onRenameStep,
  onEdgeDataChange,
  onDeleteEdge,
  onAddEdge,
}: OwnProps) => {
  const [actions, setActions] = useState<ActionContract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const stepIdEditor = useStepIdEditor(node?.id, onRenameStep);

  useEffect(() => {
    const loadActions = async () => {
      try {
        const result = await getActions();
        setActions(result);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load actions';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    void loadActions();
  }, []);

  const availableStepIds = useMemo(
    () => allNodes.map((n) => n.id).filter((id) => (node ? id !== node.id : true)),
    [allNodes, node],
  );

  const resolveStepOutputs = useCallback(
    (stepId: string): string[] => {
      if (!stepId) return [];
      const sourceNode = allNodes.find((n) => n.id === stepId);
      if (!sourceNode) return [];
      const sourceContract = actions.find((a) => a.actionId === sourceNode.data.actionId);
      if (!sourceContract || sourceContract.outputs.length === 0) return [];
      return sourceContract.outputs.map((o) => o.name);
    },
    [allNodes, actions],
  );

  const handleInputBindingChange = useCallback(
    (inputName: string, binding: InputBinding) => {
      if (!node) return;
      const currentInputs = node.data.input ?? {};
      onNodeChange({
        ...node,
        data: { ...node.data, input: { ...currentInputs, [inputName]: binding } },
      });
    },
    [node, onNodeChange],
  );

  const handleSettingChange = useCallback(
    (name: string, value: unknown) => {
      if (!node) return;
      onNodeChange({
        ...node,
        data: { ...node.data, settings: { ...node.data.settings, [name]: value } },
      });
    },
    [node, onNodeChange],
  );

  const handleRemoveExtraInput = useCallback(
    (inputName: string) => {
      if (!node) return;
      const { [inputName]: _removed, ...rest } = node.data.input;
      onNodeChange({ ...node, data: { ...node.data, input: rest } });
    },
    [node, onNodeChange],
  );

  const handleAddInput = useCallback(() => {
    if (!node) return;
    const existingNames = new Set(Object.keys(node.data.input ?? {}));
    let name = 'NewInput';
    let idx = 1;
    while (existingNames.has(name)) {
      idx += 1;
      name = `NewInput${idx}`;
    }
    handleInputBindingChange(name, { kind: 'StepOutput', stepId: '', target: '' });
  }, [node, handleInputBindingChange]);

  const handleRoutesChange = useCallback(
    (next: Route[]) => handleSettingChange('Routes', next),
    [handleSettingChange],
  );

  if (!node) {
    return <StyledEmpty>Select a node to edit its inputs and settings.</StyledEmpty>;
  }

  if (error) {
    return <StyledError>Error loading actions: {error}</StyledError>;
  }

  if (isLoading) {
    return <StyledLoading>Loading action metadata…</StyledLoading>;
  }

  const contract = actions.find((action) => action.actionId === node.data.actionId) ?? null;
  const isRouteNode = node.data.actionId === SPECIAL_ACTIONS.route;
  const routes = (node.data.settings.Routes as Route[] | undefined) ?? [];
  const hasConnectionsSection =
    node.data.actionId !== SPECIAL_ACTIONS.initial &&
    node.data.actionId !== SPECIAL_ACTIONS.startIteration;

  return (
    <div>
      <StyledStepIdInput
        type='text'
        value={stepIdEditor.draft}
        onChange={stepIdEditor.handleChange}
        onBlur={stepIdEditor.handleBlur}
        onKeyDown={stepIdEditor.handleKeyDown}
        title='Step ID — press Enter to rename, Escape to cancel'
        aria-label='Step ID'
      />
      <StyledActionLine>
        <StyledActionDisplay>
          {contract ? contract.displayName : node.data.actionId}
        </StyledActionDisplay>
        {contract && contract.displayName !== node.data.actionId && (
          <StyledActionId>({node.data.actionId})</StyledActionId>
        )}
      </StyledActionLine>
      {contract && <OutputsSection outputs={contract.outputs} />}
      <InputsSection
        contract={contract}
        inputs={node.data.input ?? {}}
        availableStepIds={availableStepIds}
        resolveStepOutputs={resolveStepOutputs}
        onBindingChange={handleInputBindingChange}
        onRemoveExtraInput={handleRemoveExtraInput}
        onAddInput={handleAddInput}
      />
      {isRouteNode ? (
        <StyledSection>
          <StyledSectionLabel>Routes</StyledSectionLabel>
          <RoutesEditor routes={routes} onChange={handleRoutesChange} />
        </StyledSection>
      ) : (
        <SettingsSection
          settings={contract?.settings ?? []}
          values={node.data.settings}
          onChange={handleSettingChange}
        />
      )}
      {hasConnectionsSection && (
        <ConnectionsList
          node={node}
          allNodes={allNodes}
          allEdges={allEdges}
          onEdgeDataChange={onEdgeDataChange}
          onDeleteEdge={onDeleteEdge}
          onAddEdge={onAddEdge}
        />
      )}
    </div>
  );
};

const MemoNodeSidePanel = React.memo(NodeSidePanel);

export { MemoNodeSidePanel as NodeSidePanel };
export default MemoNodeSidePanel;
